import {
    FETCH_ALL_AVAILABLE_DEVICE_BACKUPS_START,
    FETCH_ALL_AVAILABLE_DEVICE_BACKUPS_SUCCESS,
    FETCH_ALL_AVAILABLE_DEVICE_BACKUPS_FAIL,
    FETCH_COMPATIBLE_DEVICE_BACKUPS_START,
    FETCH_COMPATIBLE_DEVICE_BACKUPS_SUCCESS,
    FETCH_COMPATIBLE_DEVICE_BACKUPS_FAIL,
    RESTORE_DEVICE_BACKUP_START,
    RESTORE_DEVICE_BACKUP_SUCCESS,
    RESTORE_DEVICE_BACKUP_FAIL,
    REMOVE_DEVICE_BACKUP_START,
    REMOVE_DEVICE_BACKUP_SUCCESS,
    REMOVE_DEVICE_BACKUP_FAIL,
    SHOULD_LAUNCH_GARMIN_EXPRESS,
    RESET_DEVICE_BACKUPS,
    RESET_DEVICE_BACKUPS_ERRORS,
    FETCH_DEVICE_BACKUPS_SUMMARY_START,
    FETCH_DEVICE_BACKUPS_SUMMARY_SUCCESS,
    FETCH_DEVICE_BACKUPS_SUMMARY_FAIL
} from 'actions/ActionTypes.js';
import { fetchCompatibleDeviceBackupsSuccess } from 'actions/deviceBackupsActions';

/** @typedef {import('reducers/multiSportEdit/types/entity/RootState').RootState} RootState */
/** @typedef {import('reducers/device/types/DeviceBackupsState').DeviceBackupsState} DeviceBackupsState */

/** @type {DeviceBackupsState} */
const INITIAL_STATE = {
    allAvailableDeviceBackups: [],
    allAvailableBackupsFetching: false,
    allAvailableBackupsSuccess: false,
    allAvailableBackupsFail: false,
    compatibleDeviceBackups: [],
    compatibleDeviceBackupsFetching: false,
    compatibleDeviceBackupsSuccess: false,
    compatibleDeviceBackupsFail: false,
    removeBackupSuccess: false,
    removingBackup: false,
    removeBackupFail: false,
    restoreBackupSuccess: false,
    restoringBackup: false,
    restoreBackupFail: false,
    shouldLaunchGarminExpress: false,
    backupsSummary: {},
    backupsSummaryFetching: false,
    backupsSummarySuccess: false,
    backupsSummaryFail: false
};

const allAvailableDeviceBackupsFetching = (state) => ({
    ...state,
    allAvailableBackupsFetching: true
});

const allAvailableDeviceBackupsFetchFinished = (isSuccess) => (state, action) => {
    return {
        ...state,
        allAvailableDeviceBackups: isSuccess
            ? action.allAvailableDeviceBackups
            : INITIAL_STATE.allAvailableDeviceBackups,
        allAvailableBackupsFetching: false,
        allAvailableBackupsSuccess: isSuccess,
        allAvailableBackupsFail: !isSuccess
    };
};

const compatibleDeviceBackupsFetching = (state) => ({
    ...state,
    compatibleDeviceBackupsFetching: true
});

const fetchCompatibleDeviceBackupsFail = (state, action) => {
    return {
        ...state,
        compatibleDeviceBackups: INITIAL_STATE.compatibleDeviceBackups,
        compatibleDeviceBackupsFetching: false,
        compatibleDeviceBackupsSuccess: false,
        compatibleDeviceBackupsFail: true
    };
};

/**
 * @param {DeviceBackupsState} state
 * @param {ReturnType<typeof fetchCompatibleDeviceBackupsSuccess>} action
 * @returns {DeviceBackupsState}
 */
const fetchCompatibleDeviceBackupsSuccessActionHandler = (state, action) => {
    const { compatibleDeviceBackups } = action.payload;
    return {
        ...state,
        compatibleDeviceBackups: compatibleDeviceBackups,
        compatibleDeviceBackupsFetching: false,
        compatibleDeviceBackupsSuccess: true,
        compatibleDeviceBackupsFail: false
    };
};

const removingBackup = (state) => ({
    ...state,
    removingBackup: true
});

const removeBackupFinished = (isSuccess) => (state, action) => {
    const { deviceId } = action;
    const filteredBackups = state.allAvailableDeviceBackups.filter((backup) => backup.deviceId !== deviceId);
    return {
        ...state,
        allAvailableDeviceBackups:
            isSuccess && filteredBackups.length ? filteredBackups : INITIAL_STATE.allAvailableDeviceBackups,
        removingBackup: false,
        removeBackupSuccess: isSuccess,
        removeBackupFail: !isSuccess
    };
};

const restoringBackup = (state) => ({
    ...state,
    restoringBackup: true
});

const restoringBackupFinished = (isSuccess) => (state) => {
    return {
        ...state,
        compatibleDeviceBackupsFetching: false,
        restoreBackupSuccess: isSuccess,
        restoreBackupFail: !isSuccess
    };
};

const shouldLaunchGarminExpressActionHandler = (state, action) => {
    return {
        ...state,
        shouldLaunchGarminExpress: action.payload
    };
};

const resetToInitialState = () => INITIAL_STATE;

const resetErrors = (state) => {
    return {
        ...state,
        allAvailableBackupsFail: false,
        compatibleDeviceBackupsFail: false,
        removeBackupFail: false,
        restoreBackupFail: false
    };
};

const fetchBackupsSummary = (state) => ({
    ...state,
    backupsSummaryFetching: true
});

const backupsSummaryFetchFinished = (isSuccess) => (state, action) => {
    return {
        ...state,
        backupsSummaryFetching: false,
        backupsSummarySuccess: isSuccess,
        backupsSummaryFail: !isSuccess,
        backupsSummary: isSuccess ? action.backupsSummary : state.backupsSummary
    };
};

// selectors
export const isAllAvailableBackupsFetching = (state) => state.deviceBackups.allAvailableBackupsFetching;
export const isAllAvailableBackupsSuccess = (state) => state.deviceBackups.allAvailableBackupsSuccess;
export const isAllAvailableBackupsFail = (state) => state.deviceBackups.allAvailableBackupsFail;
export const getAllAvailableDeviceBackups = (state) => state.deviceBackups.allAvailableDeviceBackups;

export const isCompatibleDeviceBackupsFetching = (state) => state.deviceBackups.compatibleDeviceBackupsFetching;
export const isCompatibleDeviceBackupsSuccess = (state) => state.deviceBackups.compatibleDeviceBackupsSuccess;
export const isCompatibleDeviceBackupsFail = (state) => state.deviceBackups.compatibleDeviceBackupsFail;

export const isBackupsSummaryFetching = (state) => state.deviceBackups.backupsSummaryFetching;
export const isBackupsSummarySuccess = (state) => state.deviceBackups.backupsSummarySuccess;
export const getBackupsSummary = (state) => state.deviceBackups.backupsSummary;

/** @param {DeviceBackupsState} state  */
export const selectCompatibleDeviceBackups = (state) => state.compatibleDeviceBackups;

/**
 * @param {DeviceBackupsState} state
 * @param {number} deviceId
 */
export const selectDeviceBackupForDeviceId = (state, deviceId) => {
    const { compatibleDeviceBackups } = state;
    return compatibleDeviceBackups && compatibleDeviceBackups.find((item) => +item.deviceId === deviceId);
};

/** @param {DeviceBackupsState} state  */
export const selectIsCompatibleDeviceBackupsFetched = (state) =>
    state.compatibleDeviceBackups && state.compatibleDeviceBackups.length > 0;

export const isRemovingBackup = (state) => state.deviceBackups.removingBackup;
export const isRemoveBackupSuccess = (state) => state.deviceBackups.removeBackupSuccess;
export const isRemoveBackupFail = (state) => state.deviceBackups.removeBackupFail;

export const isRestoringBackup = (state) => state.deviceBackups.restoringBackup;
export const isRestoreBackupSuccess = (state) => state.deviceBackups.restoreBackupSuccess;
export const isRestoreBackupFail = (state) => state.deviceBackups.restoreBackupFail;

export const shouldLaunchGarminExpressSelector = (state) => state.deviceBackups.shouldLaunchGarminExpress;

const handlers = {
    [FETCH_ALL_AVAILABLE_DEVICE_BACKUPS_START]: allAvailableDeviceBackupsFetching,
    [FETCH_ALL_AVAILABLE_DEVICE_BACKUPS_SUCCESS]: allAvailableDeviceBackupsFetchFinished(true),
    [FETCH_ALL_AVAILABLE_DEVICE_BACKUPS_FAIL]: allAvailableDeviceBackupsFetchFinished(false),
    [FETCH_COMPATIBLE_DEVICE_BACKUPS_START]: compatibleDeviceBackupsFetching,
    [FETCH_COMPATIBLE_DEVICE_BACKUPS_SUCCESS]: fetchCompatibleDeviceBackupsSuccessActionHandler,
    [FETCH_COMPATIBLE_DEVICE_BACKUPS_FAIL]: fetchCompatibleDeviceBackupsFail,
    [REMOVE_DEVICE_BACKUP_START]: removingBackup,
    [REMOVE_DEVICE_BACKUP_SUCCESS]: removeBackupFinished(true),
    [REMOVE_DEVICE_BACKUP_FAIL]: removeBackupFinished(false),
    [RESTORE_DEVICE_BACKUP_START]: restoringBackup,
    [RESTORE_DEVICE_BACKUP_SUCCESS]: restoringBackupFinished(true),
    [RESTORE_DEVICE_BACKUP_FAIL]: restoringBackupFinished(false),
    [SHOULD_LAUNCH_GARMIN_EXPRESS]: shouldLaunchGarminExpressActionHandler,
    [RESET_DEVICE_BACKUPS]: resetToInitialState,
    [RESET_DEVICE_BACKUPS_ERRORS]: resetErrors,
    [FETCH_DEVICE_BACKUPS_SUMMARY_START]: fetchBackupsSummary,
    [FETCH_DEVICE_BACKUPS_SUMMARY_SUCCESS]: backupsSummaryFetchFinished(true),
    [FETCH_DEVICE_BACKUPS_SUMMARY_FAIL]: backupsSummaryFetchFinished(false)
};

export default function deviceBackups(state = INITIAL_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}
